/**
 * Public catalog search page.
 * Accessible without authentication.
 *
 * Accessibility (RGAA AA):
 * - Proper form labeling and role="search"
 * - aria-live for search results announcement
 * - aria-pressed for active filter states
 * - Visible focus indicators
 * - Semantic structure with proper headings
 * - Accessible pagination with aria-current
 */

import { useState, useCallback, useMemo, useId } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { recordTypeIcon, recordTypeLabel } from "@/lib/recordTypes";
import { BookCover } from "../components";

interface RecordSummary {
  id: string;
  type: string;
  title: string;
  subtitle: string | null;
  authors: string | null;
  publisher: string | null;
  publicationYear: string | null;
  isbn: string | null;
  language: string;
  coverUrl: string | null;
  availableCount: number;
  totalCount: number;
}

interface SearchResponse {
  records: RecordSummary[];
  total: number;
  limit: number;
  offset: number;
  facets: {
    types: Record<string, number>;
    languages: Record<string, number>;
    years: Record<number, number>;
  };
}

const LANGUAGE_LABELS: Record<string, string> = {
  fre: "Français",
  eng: "Anglais",
  ger: "Allemand",
  spa: "Espagnol",
  ita: "Italien",
  por: "Portugais",
  ara: "Arabe",
  chi: "Chinois",
  jpn: "Japonais",
  kor: "Coréen",
  rus: "Russe",
  mul: "Multilingue",
  und: "Indéterminé",
};

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(searchParams.get("q") ?? "");
  const resultsRegionId = useId();

  const query = searchParams.get("q") ?? "";
  const typeFilter = searchParams.get("type") ?? "";
  const languageFilter = searchParams.get("language") ?? "";
  const availableOnly = searchParams.get("available") === "true";
  const page = parseInt(searchParams.get("page") ?? "1", 10);
  const limit = 20;

  // Build API URL
  const apiUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (typeFilter) params.set("type", typeFilter);
    if (languageFilter) params.set("language", languageFilter);
    if (availableOnly) params.set("available", "true");
    params.set("limit", limit.toString());
    params.set("offset", ((page - 1) * limit).toString());
    return `/api/portal/catalog/search?${params.toString()}`;
  }, [query, typeFilter, languageFilter, availableOnly, page]);

  // Fetch search results
  const {
    data: searchResults,
    isLoading,
    error,
  } = useQuery<SearchResponse>({
    queryKey: ["portal", "search", apiUrl],
    queryFn: async (): Promise<SearchResponse> => {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Search failed");
      return (await response.json()) as SearchResponse;
    },
  });

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const params = new URLSearchParams(searchParams);
      if (inputValue.trim()) {
        params.set("q", inputValue.trim());
      } else {
        params.delete("q");
      }
      params.delete("page"); // Reset to first page
      setSearchParams(params);
    },
    [inputValue, searchParams, setSearchParams]
  );

  const updateFilter = useCallback(
    (key: string, value: string | null) => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      params.delete("page"); // Reset to first page
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const toggleAvailableOnly = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    if (availableOnly) {
      params.delete("available");
    } else {
      params.set("available", "true");
    }
    params.delete("page");
    setSearchParams(params);
  }, [availableOnly, searchParams, setSearchParams]);

  const goToPage = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      if (newPage > 1) {
        params.set("page", newPage.toString());
      } else {
        params.delete("page");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const totalPages = searchResults ? Math.ceil(searchResults.total / limit) : 0;

  return (
    <div className="space-y-6">
      {/* Search form */}
      <form
        onSubmit={handleSearch}
        className="flex gap-3"
        role="search"
        aria-label="Recherche dans le catalogue"
      >
        <div className="relative flex-1">
          <label htmlFor="search-input" className="sr-only">
            Rechercher dans le catalogue
          </label>
          <input
            id="search-input"
            type="search"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            placeholder="Rechercher par titre, auteur, ISBN..."
            aria-describedby={resultsRegionId}
            className="w-full rounded-lg border border-gray-300 px-4 py-3 pl-10 text-base focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <svg
            className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <button
          type="submit"
          className="rounded-lg bg-blue-600 px-6 py-3 font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
        >
          Rechercher
        </button>
      </form>

      {/* Active filters */}
      {(typeFilter || languageFilter || availableOnly) && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">Filtres actifs :</span>
          {typeFilter && (
            <button
              onClick={() => {
                updateFilter("type", null);
              }}
              className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-800 hover:bg-blue-200 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:bg-blue-900/30 dark:text-blue-300"
              aria-label={`Supprimer le filtre : ${recordTypeLabel(typeFilter)}`}
            >
              {recordTypeLabel(typeFilter)}
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          {languageFilter && (
            <button
              onClick={() => {
                updateFilter("language", null);
              }}
              className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-800 hover:bg-blue-200 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:bg-blue-900/30 dark:text-blue-300"
              aria-label={`Supprimer le filtre : ${LANGUAGE_LABELS[languageFilter] || languageFilter}`}
            >
              {LANGUAGE_LABELS[languageFilter] || languageFilter}
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          {availableOnly && (
            <button
              onClick={toggleAvailableOnly}
              className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-sm text-green-800 hover:bg-green-200 focus:ring-2 focus:ring-green-500 focus:outline-none dark:bg-green-900/30 dark:text-green-300"
              aria-label="Supprimer le filtre : Disponibles uniquement"
            >
              Disponibles uniquement
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Facets sidebar */}
        <aside className="w-full shrink-0 lg:w-64" aria-label="Filtres de recherche">
          <div className="space-y-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            {/* Availability filter */}
            <div>
              <h3 className="mb-2 font-medium text-gray-900 dark:text-white">Disponibilité</h3>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={availableOnly}
                  onChange={toggleAvailableOnly}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Disponibles uniquement
                </span>
              </label>
            </div>

            {/* Type facet */}
            {searchResults?.facets.types && Object.keys(searchResults.facets.types).length > 0 && (
              <div>
                <h3 className="mb-2 font-medium text-gray-900 dark:text-white">Type</h3>
                <ul className="space-y-1">
                  {Object.entries(searchResults.facets.types).map(([type, count]) => (
                    <li key={type}>
                      <button
                        onClick={() => {
                          updateFilter("type", typeFilter === type ? null : type);
                        }}
                        className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm ${
                          typeFilter === type
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                            : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        }`}
                      >
                        <span>
                          {recordTypeIcon(type)} {recordTypeLabel(type)}
                        </span>
                        <span className="text-xs text-gray-500">{count}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Language facet */}
            {searchResults?.facets.languages &&
              Object.keys(searchResults.facets.languages).length > 0 && (
                <div>
                  <h3 className="mb-2 font-medium text-gray-900 dark:text-white">Langue</h3>
                  <ul className="space-y-1">
                    {Object.entries(searchResults.facets.languages)
                      .slice(0, 8)
                      .map(([lang, count]) => (
                        <li key={lang}>
                          <button
                            onClick={() => {
                              updateFilter("language", languageFilter === lang ? null : lang);
                            }}
                            className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm ${
                              languageFilter === lang
                                ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                                : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                            }`}
                          >
                            <span>{LANGUAGE_LABELS[lang] || lang}</span>
                            <span className="text-xs text-gray-500">{count}</span>
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
          </div>
        </aside>

        {/* Results */}
        <div
          className="flex-1"
          role="region"
          aria-label="Résultats de recherche"
          id={resultsRegionId}
        >
          {isLoading ? (
            <div
              className="flex items-center justify-center py-12"
              aria-busy="true"
              aria-label="Chargement des résultats"
            >
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600 motion-reduce:animate-none" />
            </div>
          ) : error ? (
            <div
              className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
              role="alert"
            >
              Une erreur est survenue lors de la recherche.
            </div>
          ) : searchResults && searchResults.records.length > 0 ? (
            <>
              <p
                className="mb-4 text-sm text-gray-500 dark:text-gray-400"
                aria-live="polite"
                aria-atomic="true"
              >
                {String(searchResults.total)} résultat{searchResults.total > 1 ? "s" : ""}
                {query && (
                  <>
                    {" "}
                    pour « <span className="font-medium">{query}</span> »
                  </>
                )}
              </p>

              <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
                {searchResults.records.map((record) => (
                  <li key={record.id}>
                    <RecordRow record={record} />
                  </li>
                ))}
              </ul>

              {/* Pagination */}
              {totalPages > 1 && (
                <nav
                  className="mt-6 flex items-center justify-center gap-2"
                  aria-label="Pagination des résultats"
                >
                  <button
                    onClick={() => {
                      goToPage(page - 1);
                    }}
                    disabled={page <= 1}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
                    aria-label="Page précédente"
                  >
                    Précédent
                  </button>
                  <span className="text-sm text-gray-500 dark:text-gray-400" aria-current="page">
                    Page {String(page)} sur {String(totalPages)}
                  </span>
                  <button
                    onClick={() => {
                      goToPage(page + 1);
                    }}
                    disabled={page >= totalPages}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
                    aria-label="Page suivante"
                  >
                    Suivant
                  </button>
                </nav>
              )}
            </>
          ) : (
            <div className="py-12 text-center">
              {query ? (
                <p className="text-gray-500 dark:text-gray-400">
                  Aucun résultat pour « <span className="font-medium">{query}</span> »
                </p>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">
                  Entrez un terme de recherche ou utilisez les filtres pour explorer le catalogue.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RecordRow({ record }: { record: RecordSummary }) {
  return (
    <Link
      to={`/opac/record/${record.id}`}
      className="block p-4 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none focus:ring-inset motion-reduce:transition-none dark:hover:bg-gray-700/50"
      aria-label={`${record.title}${record.authors ? ` par ${record.authors}` : ""}. ${record.availableCount > 0 ? `${String(record.availableCount)} disponible${record.availableCount > 1 ? "s" : ""}` : "Indisponible"}`}
    >
      <div className="flex items-start gap-4">
        {/* Cover image */}
        <BookCover coverUrl={record.coverUrl} alt="" type={record.type} size="sm" />

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <span
              className="shrink-0 text-base text-gray-400 dark:text-gray-500"
              aria-hidden="true"
            >
              {recordTypeIcon(record.type)}
            </span>
            <h3 className="font-medium text-gray-900 dark:text-white">{record.title}</h3>
          </div>
          {record.subtitle && (
            <p className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">
              {record.subtitle}
            </p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
            {record.authors && <span>{record.authors}</span>}
            {record.publisher && (
              <>
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <span>{record.publisher}</span>
              </>
            )}
            {record.publicationYear && (
              <>
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <span>{record.publicationYear}</span>
              </>
            )}
          </div>
          {record.isbn && (
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">ISBN {record.isbn}</p>
          )}
        </div>

        {/* Availability */}
        <div className="shrink-0 text-right">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
              record.availableCount > 0
                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                : record.totalCount > 0
                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                  : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
            }`}
          >
            {record.availableCount > 0 ? (
              <>
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                {record.availableCount} disponible{record.availableCount > 1 ? "s" : ""}
              </>
            ) : record.totalCount > 0 ? (
              <>
                <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                Emprunté
              </>
            ) : (
              "Pas d'exemplaire"
            )}
          </span>
          {record.totalCount > 0 && (
            <p className="mt-1 text-xs text-gray-400">
              {record.totalCount} exemplaire{record.totalCount > 1 ? "s" : ""}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

export default SearchPage;
